import React, { useState } from "react";
import { Template } from "@/lib/types";
import { DialogFooter } from "@/components/ui/dialog";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { TemplatePreview } from "@/pages/template/shared/TemplatePreview";
import { cn } from "@/lib/utils";
import { getCheckeredBg } from "@/lib/tailwindUtils";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  DynamicTextInfo,
  extractDynamicImagesFromCanvasData,
  extractDynamicTextFromCanvasData,
  removeMustache,
  replaceImageInCanvasData,
} from "@/components/canvas/utils/canvasUtils";
import { DynamicTemplateDataSourceResponseItem, DynamicTemplateLayoutConfig } from "./contentTypes";
import { LoadingSmall } from "@/components/LoadingSmall";
import {
  CreateFromTemplateContentPropsForm,
  FormSchemaType as ContentPropsFormSchemaType,
} from "./CreateFromTemplateContentPropsForm";
import { addContentDynamicFromTemplate } from "@/sideEffects/api";
import { useMutation } from "@tanstack/react-query";
import { showApiError } from "@/lib/toast";
import { useSelector } from "react-redux";
import { selectActiveFirm } from "@/store/appSelectors";
import { CreateFromTemplateDynamicImagesForm } from "./CreateFromTemplateDynamicImagesForm";
import { CanvasData } from "@/components/canvas/types/canvasTypes";

const formSchema = z.object({
  dataSourceUrl: z.string().min(1),
  pollingIntervalSeconds: z.string().min(1).optional(),
});

type FormSchemaType = z.infer<typeof formSchema>;

interface Props {
  template: Template;
  onClose: () => void;
  onUploadSuccess: () => void;
}

const constructExampleDataSourceResponse = (
  dynamicTexts: DynamicTextInfo[]
): DynamicTemplateDataSourceResponseItem[] => {
  const expectedDataSourceResponse: DynamicTemplateDataSourceResponseItem[] = [];
  dynamicTexts.map((textObj) => {
    textObj.dynamicValues.forEach((dynamicValue) => {
      expectedDataSourceResponse.push({
        key: removeMustache(dynamicValue),
        value: "123 etc..",
      });
    });
  });
  return expectedDataSourceResponse;
};

const DEFAULT_POLLING_INTERVAL_SECONDS = 30;

export const CreateFromTemplatePreviewAndConfigDialogDynamicTab: React.FC<Props> = ({
  template,
  onClose,
  onUploadSuccess,
}) => {
  const { t } = useTranslation();
  const activeFirm = useSelector(selectActiveFirm);
  const [replacedImages, setReplacedImages] = useState<Record<string, string>>({});
  const dynamicImages = extractDynamicImagesFromCanvasData(template.canvas_data);

  const form = useForm<FormSchemaType>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      dataSourceUrl: "",
      pollingIntervalSeconds: DEFAULT_POLLING_INTERVAL_SECONDS.toString(),
    },
  });

  const [contentProps, setContentProps] = useState<ContentPropsFormSchemaType>({
    name: "",
    startDate: "",
    endDate: "",
    screenSizeId: 0,
    duration: 0,
  });
  const [contentPropsErrors, setContentPropsErrors] = useState<any>({});

  const addMutation = useMutation({
    mutationFn: addContentDynamicFromTemplate,
    onSuccess: () => {
      onUploadSuccess();
      onClose();
    },
    onError: showApiError,
  });

  const getPreviewCanvasData = (): CanvasData => {
    let tempCanvasData = JSON.parse(JSON.stringify(template.canvas_data));
    for (const [oldImageSrc, newImageSrc] of Object.entries(replacedImages)) {
      tempCanvasData = replaceImageInCanvasData(tempCanvasData, oldImageSrc, newImageSrc);
    }
    return tempCanvasData;
  };

  const onSubmit = (values: FormSchemaType) => {
    if (!template) {
      return;
    }
    const layoutConfig: DynamicTemplateLayoutConfig = {
      templateId: template.id,
      templateCanvasData: JSON.stringify(getPreviewCanvasData()),
      dataSourceUrl: values.dataSourceUrl,
      pollingIntervalSeconds: Number(values.pollingIntervalSeconds) ?? DEFAULT_POLLING_INTERVAL_SECONDS,
    };

    addMutation.mutate({
      firmGuid: String(activeFirm?.firmGuid),
      name: contentProps.name,
      startDate: contentProps.startDate,
      endDate: contentProps.endDate,
      screenSizeId: contentProps.screenSizeId,
      layoutConfig: JSON.stringify(layoutConfig),
      duration: contentProps.duration,
    });
  };

  const dynamicTexts = template ? extractDynamicTextFromCanvasData(template.canvas_data) : [];
  const expectedDataSourceResponse = constructExampleDataSourceResponse(dynamicTexts);

  const isButtonDisabled = Object.keys(contentPropsErrors).length > 0 || addMutation.isPending;

  const handleImageReplace = (oldImageSrc: string, newImageSrc: string) => {
    setReplacedImages({ ...replacedImages, [oldImageSrc]: newImageSrc });
  };

  return (
    <div>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <div className="flex items-start space-x-4">
            <div className="w-1/3">
              <div className={cn(`rounded-md overflow-hidden border h-[500px]`, getCheckeredBg())}>
                <TemplatePreview data={getPreviewCanvasData()} previewSize="full" />
              </div>
            </div>
            <div className="w-2/3">
              {dynamicImages.length > 0 && (
                <CreateFromTemplateDynamicImagesForm dynamicImages={dynamicImages} onReplace={handleImageReplace} />
              )}

              <CreateFromTemplateContentPropsForm
                onFormValuesChange={setContentProps}
                onFormErrorsChange={setContentPropsErrors}
              />

              <div>
                <FormField
                  control={form.control}
                  name="dataSourceUrl"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t("splitContent.dataSourceUrl")}</FormLabel>
                      <FormControl>
                        <Input placeholder={t("splitContent.dataSourceUrl")} {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="pollingIntervalSeconds"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t("splitContent.pollingIntervalSeconds")}</FormLabel>
                      <FormControl>
                        <Input placeholder={t("splitContent.pollingIntervalSeconds")} type="number" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                {dynamicTexts.length ? (
                  <>
                    <div className="mt-2 border p-2 rounded-md bg-muted">
                      {dynamicTexts.map((textObj) => (
                        <div key={textObj.indexInCanvasData} className="flex flex-row gap-2 items-center">
                          <span className="w-[120px]">{t("Binding text")}</span>
                          <span className="text-sm break-words">
                            {template.canvas_data.objects[textObj.indexInCanvasData].text}
                          </span>
                        </div>
                      ))}
                    </div>
                    <Alert className="mt-2" variant="info">
                      <AlertTitle>{t("Expected Data source response format based on template:")}</AlertTitle>
                      <AlertDescription>
                        <pre className="overflow-x-auto text-xs md:text-sm">
                          {JSON.stringify(expectedDataSourceResponse, null, 2)}
                        </pre>
                      </AlertDescription>
                    </Alert>
                  </>
                ) : null}
              </div>
            </div>
          </div>
          <DialogFooter className="mt-6">
            <Button onClick={onClose} variant="outline">
              {t("common.cancel")}
            </Button>
            <Button type="submit" className="w-full md:w-auto" disabled={isButtonDisabled}>
              {t("content.generateContent")} {addMutation.isPending && <LoadingSmall />}
            </Button>
          </DialogFooter>
        </form>
      </Form>
    </div>
  );
};
